import { memo } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  BadgeDollarSign,
  Bot,
  Box,
  Braces,
  CalendarDays,
  ClipboardList,
  Clock3,
  CreditCard,
  Database,
  FileText,
  Frame,
  GitBranch,
  Globe2,
  Handshake,
  Magnet,
  Mail,
  Megaphone,
  MessageCircle,
  PanelsTopLeft,
  Phone,
  RefreshCw,
  Share2,
  Sparkles,
  StickyNote,
  TrendingUp,
  UserMinus,
  UserRound,
  Webhook,
  Workflow,
} from 'lucide-react';
import { Handle, NodeResizer, Position, type NodeProps } from '@xyflow/react';
import type { EditorNode } from '../../domain/editor-graph';
import { CATEGORY_LABELS, getNodeDefinition, STATUS_LABELS } from '../registry/node-registry';

const colorOverrides: Record<string, string> = {
  neutral: '#9aa09b',
  blue: '#2f9bff',
  green: '#2fc87b',
  amber: '#eaa52d',
  violet: '#8666f5',
};
type NodeIcon = LucideIcon;
const icons: Record<string, NodeIcon> = {
  BadgeDollarSign,
  Bot,
  Box,
  Braces,
  CalendarDays,
  ClipboardList,
  Clock3,
  CreditCard,
  Database,
  FileText,
  Frame,
  GitBranch,
  Globe2,
  Handshake,
  Magnet,
  Mail,
  Megaphone,
  MessageCircle,
  PanelsTopLeft,
  Phone,
  RefreshCw,
  Share2,
  Sparkles,
  StickyNote,
  TrendingUp,
  UserMinus,
  UserRound,
  Webhook,
  Workflow,
};
function iconFor(name: string): NodeIcon {
  return icons[name] ?? Box;
}

export const SemanticNode = memo(function SemanticNode({
  id,
  type,
  data,
  selected,
}: NodeProps<EditorNode>) {
  const definition = getNodeDefinition(type ?? '');
  const Icon = iconFor(definition?.icon ?? 'Box');
  const canConnectIn = definition?.capabilities.canConnectIn ?? false;
  const canConnectOut = definition?.capabilities.canConnectOut ?? false;
  const branches =
    type === 'condition' && Array.isArray(data.branches)
      ? (data.branches as unknown[]).filter((value): value is string => typeof value === 'string')
      : null;
  const status =
    typeof data.status === 'string' && data.status in STATUS_LABELS
      ? (data.status as keyof typeof STATUS_LABELS)
      : 'active';
  const name =
    typeof data.name === 'string' && data.name.trim() ? data.name : (definition?.label ?? 'Node');
  const accent =
    typeof data.colorOverride === 'string'
      ? colorOverrides[data.colorOverride]
      : `var(--node-${definition?.category ?? 'documentation'})`;
  const isFrame = type === 'group-frame';
  const isNote = type === 'note';
  return (
    <div
      className={`${isFrame ? 'h-full min-h-[160px] min-w-[280px] bg-transparent' : isNote ? 'min-w-[220px] bg-[#18160e]' : 'min-w-[176px] bg-[#111412]'} rounded-lg border px-3 py-3 shadow-xl transition-colors`}
      style={{
        borderColor: selected ? 'var(--df-text-brand)' : accent,
        borderLeftWidth: isFrame ? 1 : 4,
      }}
      data-testid="semantic-node"
      aria-label={`${definition?.label ?? 'Unknown'} node: ${name}`}
    >
      {isFrame ? (
        <NodeResizer
          minWidth={280}
          minHeight={160}
          isVisible={selected}
          color="var(--df-text-brand)"
        />
      ) : null}
      {!isFrame && canConnectIn ? (
        <Handle
          type="target"
          position={Position.Left}
          isConnectable={canConnectIn}
          className="!h-2 !w-2 !border !bg-[#111412]"
        />
      ) : null}
      <div className="flex items-center gap-2">
        <Icon aria-hidden size={15} className="shrink-0" />
        <span className="max-w-[180px] truncate text-sm font-medium text-text-primary">{name}</span>
      </div>
      {!isFrame ? (
        <div className="mt-2 flex items-center justify-between gap-2 text-[10px] uppercase tracking-wider text-text-muted">
          <span>{definition ? CATEGORY_LABELS[definition.category] : 'Desconhecida'}</span>
          <span aria-label={`Status: ${STATUS_LABELS[status]}`}>{STATUS_LABELS[status]}</span>
        </div>
      ) : null}
      {isNote && typeof data.notes === 'string' && data.notes ? (
        <p className="mt-3 max-w-[260px] whitespace-pre-wrap text-xs text-text-secondary">
          {data.notes}
        </p>
      ) : null}
      {!isFrame && canConnectOut && branches ? (
        <>
          {branches.map((branch, index) => (
            <Handle
              key={branch}
              id={branch}
              type="source"
              position={Position.Right}
              isConnectable={canConnectOut}
              style={{ top: `${((index + 1) / (branches.length + 1)) * 100}%` }}
              className="!h-2 !w-2 !border !bg-[#111412]"
            />
          ))}
        </>
      ) : !isFrame && canConnectOut ? (
        <Handle
          type="source"
          position={Position.Right}
          isConnectable={canConnectOut}
          className="!h-2 !w-2 !border !bg-[#111412]"
        />
      ) : null}
      {branches ? (
        <div className="mt-2 flex flex-col gap-1 text-[10px] text-text-muted">
          {branches.map((branch) => (
            <span key={branch} className="text-right">
              {branch} →
            </span>
          ))}
        </div>
      ) : null}
      <span className="sr-only">Node ID {id}</span>
    </div>
  );
});
